/**
 * Haptics utility for mobile tactile feedback
 * Uses the Vibration API (navigator.vibrate)
 * Falls back gracefully on unsupported devices
 *
 * Chrome refuses navigator.vibrate() until the user has interacted with the
 * page ("Blocked call to navigator.vibrate because user hasn't tapped on the
 * frame") and logs that refusal as a console ERROR — so a modal that
 * auto-opens on load (IMO/Agency confirmation, account-locked) would log that
 * error through its shown.bs.modal haptic. `canVibrate` skips the call until
 * the sticky user activation exists; browsers without the User Activation API
 * always try.
 */
const canVibrate = () =>
  typeof navigator.vibrate === "function" &&
  (navigator.userActivation?.hasBeenActive ?? true)

export const Haptics = {
  /**
   * Check if vibration is supported
   * @returns {boolean} true if Vibration API is available
   */
  isSupported: () => 'vibrate' in navigator,

  /**
   * Light feedback for selections/toggles (25ms)
   * @returns {boolean|undefined} true if vibration started, false if not supported/not yet allowed, undefined if navigator.vibrate is missing
   */
  light: () => Haptics.pulse(25),

  /**
   * Medium feedback for navigation/actions (50ms)
   * @returns {boolean|undefined} true if vibration started, false if not supported/not yet allowed, undefined if navigator.vibrate is missing
   */
  medium: () => Haptics.pulse(50),

  /**
   * Heavy feedback for important confirmations (75ms)
   * @returns {boolean|undefined} true if vibration started, false if not supported/not yet allowed, undefined if navigator.vibrate is missing
   */
  heavy: () => Haptics.pulse(75),

  /**
   * Double tap pattern for toggle actions [vibrate, pause, vibrate]
   * @returns {boolean|undefined} true if vibration started, false if not supported/not yet allowed, undefined if navigator.vibrate is missing
   */
  double: () => Haptics.pulse([20, 30, 20]),

  /**
   * Success pattern for completed actions
   * @returns {boolean|undefined} true if vibration started, false if not supported/not yet allowed, undefined if navigator.vibrate is missing
   */
  success: () => Haptics.pulse([30, 50, 30]),

  /**
   * Error/warning pattern
   * @returns {boolean|undefined} true if vibration started, false if not supported/not yet allowed, undefined if navigator.vibrate is missing
   */
  error: () => Haptics.pulse([50, 30, 50, 30, 50]),

  /**
   * One vibration (a duration or a pattern), gated on user activation.
   * @param {number|number[]} pattern
   * @returns {boolean|undefined}
   */
  pulse: (pattern) => {
    if (typeof navigator.vibrate !== "function") return undefined
    if (!canVibrate()) return false
    return navigator.vibrate(pattern)
  },
}
